import { useCallback, useEffect, useState } from 'react';
import { useFocusEffect } from 'expo-router';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { fetchPendingReviewCount } from '../services/importReviewService';

/**
 * How many collection-imported recipes are waiting for review.
 *
 * Drives the admin-only badge on the recipes screen. Refetches on focus so the
 * count is right when returning from the review screen; `enabled` short-circuits
 * the query entirely for non-admins so they never pay for it.
 */
export function useRecipeReviewCount(enabled: boolean): number {
  const user = useSelector((state: RootState) => state.auth.user);
  // Refetch when the household loads: the queue lives in its collection.
  const householdId = useSelector((state: RootState) => state.auth.householdId);
  const [count, setCount] = useState(0);

  const refresh = useCallback(() => {
    if (!enabled || !user?.uid) {
      setCount(0);
      return;
    }
    let cancelled = false;
    fetchPendingReviewCount(user.uid, householdId)
      .then((n) => { if (!cancelled) setCount(n); })
      .catch(() => { if (!cancelled) setCount(0); });
    return () => { cancelled = true; };
  }, [enabled, user?.uid, householdId]);

  useEffect(() => refresh(), [refresh]);
  useFocusEffect(refresh);

  return count;
}
